import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Paper, Typography,} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {ExpandMore} from '@material-ui/icons';
import {Alert} from '@material-ui/lab';
import {AutoCompleteOptions, DateInput,} from '@super-template/core/src/formFields';
import messages from '@super-template/messages';
import {format, isValid} from 'date-fns';
import {pickBy} from 'lodash';
import {useContext, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useFormState} from 'react-use-form-state';
import {SettingsContext} from '../application/utils';
import useLocale from '@super-template/core/src/locale/useLocale';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  filterHeader: {
    margin: `0 ${theme.spacing(2)}px ${theme.spacing(1)}px`,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  form: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  box: {
    '& >*': {
      margin: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      width: 'auto',
      flexGrow: 1,
      flexBasis: 0,
      minWidth: 300,
      '& ::placeholder': {
        textOverflow: 'ellipsis',
      },
      [theme.breakpoints.down('md')]: {
        minWidth: '40%',
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
      },
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  buttonContainer: {
    '& button': {
      marginTop: 'auto',
      minWidth: 100,
      '&:first-child': {
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
          marginBottom: theme.spacing(2),
        },
      },
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  accordionRoot: {
    background: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[0],
    marginBottom: theme.spacing(1),
    '&::before': {
      display: 'none',
    },
  },
  accordionDetails: {
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
}));

const MobileWrapper = ({isMobile, isHolidayOrganization, children}) => {
  const intl = useIntl();
  const classes = useStyles();
  return (
    <>
      {isMobile ?
        <Accordion className={classes.accordionRoot}>
          <AccordionSummary
            expandIcon={<ExpandMore/>} aria-label={intl.formatMessage(messages.expand)} aria-controls={'filter_content'}
            id={'filter_header'}>
            <Typography variant="h6" component="h2">
              <FormattedMessage {...isHolidayOrganization ? messages.filters : messages.filters_c} />
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {children}
          </AccordionDetails>
        </Accordion>
        : children}
    </>
  );
};

export default function Filter({showPartner, setFilter, filter, isMobile}) {
  const classes = useStyles();
  const intl = useIntl();
  const [locale] = useLocale();
  const {themes, resorts, partners, organization: {isHolidayOrganization}} = useContext(SettingsContext);
  const [err, setErr] = useState(false);
  const initialState = {begin: null, resort: '', theme: '', partner: ''};
  const [formState] = useFormState({...initialState, ...pickBy(filter)});

  const empty = () => {
    Object.entries(initialState).forEach(([key, value]) =>
      formState.setField(key, value)
    );
    if (err) setErr(false);
    setFilter({}, 'replace');
  };

  const setDate = (date, field) => {
    //custom set date value, so errors display correctly
    isValid(date) ? formState.setField(field, format(date, 'yyyy-MM-dd')) :
      date ? formState.setField(field, date) :
        formState.setField(field, null);
  };

  const validDate = (key) => {
    //check if datepicker values are valid
    const fields = document.getElementsByName(key);
    return fields.length > 0 ? fields[0].getAttribute('aria-invalid') !== 'true' : false;
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (validDate('begin')) {
      setErr(false);
      setFilter(pickBy({...formState.values, page: 1}), 'replace');
      const table = document.getElementById(isMobile ? "holidayAccordion" : "holidayTable");
      if (table) table.scrollIntoView({behavior: 'smooth'});
    } else setErr(true);
  };

  return (
    <MobileWrapper isMobile={isMobile} isHolidayOrganization={isHolidayOrganization}>
      <Grid container component={Paper} className={classes.root}>
        {err &&
        <Grid item xs={12}>
          <Alert severity={'error'} fullWidth><FormattedMessage {...messages.check_filter} /></Alert>
        </Grid>}
        <form className={classes.form} onSubmit={onSubmit}>
          <Typography variant="h6" component="h2" className={classes.filterHeader}>
            <FormattedMessage {...isHolidayOrganization ? messages.filters : messages.filters_c} />
          </Typography>
          <Box display="flex" width={'100%'} justifyContent={'space-between'} flexWrap={'wrap'} className={classes.box}>
            <DateInput
              id="filter_start"
              onChange={(date) => setDate(date, 'begin')}
              fullWidth
              clearable="true"
              value={formState.values.begin}
              label={intl.formatMessage(messages.start_date)}
              name={'begin'}
              minDateMessage={intl.formatMessage(messages.date_in_past)}
              maxDateMessage={intl.formatMessage(messages.date_invalid)}
              disablePast
              KeyboardButtonProps={{'aria-label': intl.formatMessage(messages.change_date)}}/>
            <AutoCompleteOptions
              label={<FormattedMessage {...messages.location} />}
              setValue={formState.setField}
              name="resort"
              value={formState.values.resort?.toString()}
              options={[{
                name: intl.formatMessage(isHolidayOrganization ? messages.all_locations : messages.all_locations_c),
                id: ''
              }, ...resorts]}/>
            <AutoCompleteOptions
              translateTo={locale}
              label={<FormattedMessage {...messages.theme} />}
              setValue={formState.setField}
              name="theme"
              value={formState.values.theme?.toString()}
              options={[{
                name: intl.formatMessage(isHolidayOrganization ? messages.all_themes : messages.all_themes_c),
                id: ''
              }, ...themes]}/>
            {showPartner &&
            <AutoCompleteOptions
              label={<FormattedMessage {...messages.partner} />}
              setValue={formState.setField}
              name="partner"
              value={formState.values.partner?.toString()}
              options={[{name: intl.formatMessage(messages.all_partners), id: ''}, ...partners,]}/>}
            <Box display="flex" justifyContent={'flex-end'} className={classes.buttonContainer}>
              <Button variant="outlined" color="primary" onClick={empty}>
                <FormattedMessage {...messages.empty} />
              </Button>
              <Button variant="contained" type={'submit'} color="primary">
                <FormattedMessage {...messages.search} />
              </Button>
            </Box>
          </Box>
        </form>
      </Grid>
    </MobileWrapper>
  );
}
